import type { SystemVersionDTO } from "@repo/codegen/teashop-base-api"
import type {
  RouteLocationNormalizedLoaded,
  RouteLocationRaw,
  Router,
  RouteRecordRaw,
} from "vue-router"
import type { OrganizationScope } from "@/types/organization-scope"
import type { RouteAccessContext } from "@/utils/permissionRoutes"
import { recordBusinessContextTrace } from "@/utils/businessContext/diagnostics"
import { getToken as getCurrentToken } from "@/utils/cache/cookies"
import { createRouteAccessContext, ensureActiveScopeAllowedForPermissions } from "@/utils/hqMenuAccess"
import { isOrganizationScopeSwitchable } from "@/utils/organizationScopeSwitch"
import {
  clearObservedScopeToken,
  isObservedScopeToken,
  markObservedScopeToken,
} from "@/utils/tokenScopeSession"
import { resolveBootstrapScope } from "./bootstrapScope"
import {
  captureBusinessContextEpoch,
  isBusinessContextEpochCurrent,
  resetBusinessContextEpochForTest,
} from "./epoch"
import {
  isCrossViewSystemSwitch,
  navigateAfterBusinessContextSwitch,
  resolveScopeById,
} from "./navigation"
import {
  type BusinessContextRequestBarrierResolution,
  type BusinessContextRequestPolicy,
  useBusinessContextRequestBarrier,
} from "./requestBarrier"
import {
  advanceBusinessContextRevision,
  resetBusinessContextRevisionForTest,
} from "./revision"
import { consumeScopeHandoff, writeScopeHandoff } from "./scopeHandoff"
import {
  activateBusinessContextSystemVersion,
  type BusinessContextSystemVersionActivation,
  captureSystemVersionRuntime,
  clearSystemVersionCache,
  finalizeBusinessContextSystemVersion,
  invalidateSystemVersionRuntime,
  loadSystemVersionForOrganizationScope,
  restoreSystemVersionRuntime,
} from "./systemVersion"

export type BusinessContextDiscardReason = "token-changed" | "scope-changed" | "no-allowed-scope"

function resolveBusinessContextAccountId(...candidates: Array<string | null | undefined>) {
  return candidates
    .map(candidate => candidate?.trim() ?? "")
    .find(Boolean) ?? ""
}

export type BusinessContextSyncResult =
  | {
    applied: true
    reason: string
    activeScopeId: string
    frontendReloadRequired: boolean
    frontendReloadSuppressed?: boolean
    commitPreparedContext?: () => void
    getActiveRoleView?: () => any
    getDefaultHomePath?: () => string | undefined
    getBusinessContextAccountId?: () => string
  }
  | {
    applied: false
    reason: string
    discardReason: BusinessContextDiscardReason
    suggestedScopeId?: string
  }

export type BusinessContextRouterLike = {
  addRoute: (route: RouteRecordRaw) => unknown
  hasRoute?: (name: NonNullable<RouteRecordRaw["name"]>) => boolean
}

export type BusinessContextViewScopeStoreLike = {
  accountId?: string
  activeScopeId: string
  preferredScopeId?: string
  legacyArtifactHandoffScopeId?: string
  activeScope?: OrganizationScope
  organizations?: OrganizationScope[]
  setActive: (id: string, source?: string) => void
  setOrganizations?: (items: OrganizationScope[], options?: { autoSelect?: boolean }) => void
  commitActive?: (id?: string) => boolean
  commitContext?: (accountId: string, id?: string) => boolean
  bindAccount?: (
    accountId: string,
    options?: { preserveRuntimeOnAccountChange?: boolean },
  ) => boolean | void
  bumpContextVersion?: () => void
  beginContextSwitch?: () => void
  endContextSwitch?: () => void
}

export interface PreparedBusinessContextUserState {
  shopBootstrapComplete?: boolean
  stageActiveRoleView?: (roleView: any) => boolean
  getActiveRoleList: () => any[]
  getActivePermissionList: () => string[]
  getActiveRoleView?: () => any
  getDefaultHomePath?: () => string | undefined
  getBusinessContextAccountId?: () => string
  commit: () => void
}

export type BusinessContextUserStoreLike = {
  switchScopeToken?: (scopeId: string, roleView?: any, options?: any) => Promise<unknown>
  prepareBusinessContext?: (options?: any) => Promise<PreparedBusinessContextUserState>
  getInfo: (ws?: boolean, options?: any) => Promise<unknown>
  fetchShopInfo?: (
    businessContextSystemVersion?: SystemVersionDTO,
    guard?: {
      isCurrent?: () => boolean
      scope?: OrganizationScope
      scopeId?: string
      skipBusinessContextBarrier?: boolean
    },
  ) => Promise<unknown>
  getActiveRoleList: () => any[]
  getActivePermissionList: (scope?: OrganizationScope, scopeId?: string) => string[]
  getActiveRoleView?: () => any
  getDefaultHomePath?: () => string | undefined
  getPreferredRoleView?: (scopeId: string) => any
  stageActiveRoleView?: (
    roleView: any,
    scope?: OrganizationScope,
    scopeId?: string,
  ) => boolean
  setActiveRoleView?: (roleView: any) => boolean | void
  createRoleViewPreferenceRollback?: (scopeId: string) => (() => boolean | void)
  getBusinessContextAccountId?: () => string
  syncBusinessWebSocket?: () => void | Promise<void>
  disconnectBusinessWebSocket?: () => void
  clearScopedPermissionState?: () => void
  createScopeSwitchRollback?: () => ((expectedToken?: string) => boolean | void)
}

export type BusinessContextPermissionStoreLike = {
  setRoutes: (roles: any[], permissionList: string[], context?: RouteAccessContext) => void
  dynamicRoutes: RouteRecordRaw[]
  canAccessRoute?: (route: RouteLocationNormalizedLoaded) => boolean
  hasRouteName?: (name: string | symbol) => boolean
  getFirstAccessibleRoute?: () => RouteRecordRaw | undefined
}

export type BusinessContextTagsViewStoreLike = {
  delAllVisitedViews: () => void
  delAllCachedViews: () => void
}

export interface BusinessContextDependencies {
  getToken: () => string | undefined
  router: BusinessContextRouterLike
  resetRouter: () => void
  viewScopeStore: BusinessContextViewScopeStoreLike
  userStore: BusinessContextUserStoreLike
  permissionStore: BusinessContextPermissionStoreLike
  tagsViewStore: BusinessContextTagsViewStoreLike
}

export interface BusinessContextSyncOptions {
  reason: string
  loadOrganizations?: (
    requestOptions?: { skipBusinessContextBarrier?: boolean },
  ) => Promise<OrganizationScope[] | undefined>
  getInfoOptions?: any
  syncSystemVersion?: boolean
  syncWebSocket?: boolean
  clearTags?: boolean
  bumpContextVersion?: boolean
  rollbackOnFailure?: boolean
  roleView?: any
  strictRoleView?: boolean
  ensureScopeToken?: boolean
  deferFrontendReload?: boolean
  deferUserCommit?: boolean
  targetScopeId?: string
  targetScope?: OrganizationScope
}

export interface OrganizationSessionLoadOptions<T = OrganizationScope[]> {
  getToken: () => string | undefined
  loadOrganizations: (
    requestOptions?: { skipBusinessContextBarrier?: boolean },
  ) => Promise<T>
  applyOrganizations?: (organizations: T) => void
  requestOptions?: { skipBusinessContextBarrier?: boolean }
}

export interface OrganizationContextSnapshot {
  organizations: OrganizationScope[]
  currentScope?: {
    scopeId?: string
  }
}

export interface BusinessContextTransaction {
  id: number
  key: string
  epoch: number
  isCurrent: () => boolean
  assertCurrent: () => void
}

let businessContextTransactionSequence = 0
let activeBusinessContextTransactionId = 0
let businessContextTransactionTail: Promise<unknown> = Promise.resolve()
let businessContextCommandSequence = 0
let latestBusinessContextCommandId = 0
let businessContextCommandTail: Promise<unknown> = Promise.resolve()
let pendingBusinessContextTransactionCount = 0
let settleBusinessContextRequestBarrier:
  | {
    resolve: (value: BusinessContextRequestBarrierResolution) => void
    reject: (reason?: unknown) => void
  }
  | undefined
let businessContextRequestBarrierError: unknown
let businessContextRequestPolicy: BusinessContextRequestPolicy = "rebind"

function resolveTransactionRequestPolicy(key: string): BusinessContextRequestPolicy {
  return key === "refresh-token" ? "rebind" : "cancel-stale"
}

function enqueueBusinessContextRequestBarrier(key: string) {
  pendingBusinessContextTransactionCount += 1
  const requestPolicy = resolveTransactionRequestPolicy(key)
  if (settleBusinessContextRequestBarrier) {
    if (requestPolicy === "cancel-stale") {
      businessContextRequestPolicy = "cancel-stale"
    }
    return
  }
  // Close ownership immediately when the first migration is enqueued. Without
  // this boundary, an old response already queued by the browser could publish
  // between the public command and the transaction-tail microtask.
  advanceBusinessContextRevision()
  businessContextRequestPolicy = requestPolicy
  const requestBarrier = new Promise<BusinessContextRequestBarrierResolution>((resolve, reject) => {
    settleBusinessContextRequestBarrier = { resolve, reject }
  })
  useBusinessContextRequestBarrier().registerRequestBarrier(requestBarrier)
}

function finishBusinessContextRequestBarrier(error?: unknown) {
  if (error !== undefined && businessContextRequestBarrierError === undefined) {
    businessContextRequestBarrierError = error
  }
  pendingBusinessContextTransactionCount = Math.max(
    0,
    pendingBusinessContextTransactionCount - 1,
  )
  if (pendingBusinessContextTransactionCount > 0) return

  const settlement = settleBusinessContextRequestBarrier
  settleBusinessContextRequestBarrier = undefined
  if (businessContextRequestBarrierError === undefined) {
    settlement?.resolve({ requestPolicy: businessContextRequestPolicy })
  } else {
    settlement?.reject(businessContextRequestBarrierError)
  }
  businessContextRequestBarrierError = undefined
  businessContextRequestPolicy = "rebind"
}

export function runBusinessContextTransaction<T>(
  key: string,
  operation: (transaction: BusinessContextTransaction) => Promise<T>,
  options: { manageRequestBarrier?: boolean } = {},
): Promise<T> {
  const manageRequestBarrier = options.manageRequestBarrier !== false
  if (manageRequestBarrier) {
    enqueueBusinessContextRequestBarrier(key)
  }
  const id = ++businessContextTransactionSequence
  const epoch = captureBusinessContextEpoch()
  const transaction: BusinessContextTransaction = {
    id,
    key,
    epoch,
    isCurrent: () =>
      activeBusinessContextTransactionId === id
      && isBusinessContextEpochCurrent(epoch),
    assertCurrent: () => {
      if (
        activeBusinessContextTransactionId !== id
        || !isBusinessContextEpochCurrent(epoch)
      ) {
        throw createAuthSessionChangedError()
      }
    },
  }
  const promise = businessContextTransactionTail
    .catch(() => undefined)
    .then(async () => {
      if (!isBusinessContextEpochCurrent(epoch)) {
        throw createAuthSessionChangedError()
      }
      activeBusinessContextTransactionId = id
      advanceBusinessContextRevision()
      try {
        return await operation(transaction)
      } finally {
        advanceBusinessContextRevision()
      }
    })
  let transactionError: unknown
  const trackedPromise = promise.catch((error) => {
    transactionError = error
    throw error
  })
    .finally(() => {
      if (activeBusinessContextTransactionId === id) {
        activeBusinessContextTransactionId = 0
      }
      if (manageRequestBarrier) {
        finishBusinessContextRequestBarrier(transactionError)
      }
    })
  businessContextTransactionTail = trackedPromise
  return trackedPromise
}

export function coordinateBusinessContextRefresh<T>(
  operation: (transaction: BusinessContextTransaction) => Promise<T>,
) {
  return runBusinessContextTransaction("refresh-token", operation)
}

export function resetBusinessContextManagerForTest() {
  resetBusinessContextEpochForTest()
  businessContextTransactionSequence += 1
  activeBusinessContextTransactionId = 0
  businessContextTransactionTail = Promise.resolve()
  businessContextCommandSequence += 1
  latestBusinessContextCommandId = businessContextCommandSequence
  businessContextCommandTail = Promise.resolve()
  pendingBusinessContextTransactionCount = 0
  settleBusinessContextRequestBarrier?.resolve({ requestPolicy: "cancel-stale" })
  settleBusinessContextRequestBarrier = undefined
  businessContextRequestBarrierError = undefined
  businessContextRequestPolicy = "rebind"
  useBusinessContextRequestBarrier().clearRequestBarrierForTest()
  resetBusinessContextRevisionForTest()
}

export function stageBusinessContextOrganizations(
  viewScopeStore: Pick<BusinessContextViewScopeStoreLike, "setOrganizations">,
  organizations: OrganizationScope[],
) {
  viewScopeStore.setOrganizations?.(organizations, { autoSelect: false })
}

export function stageBusinessContextScope(
  viewScopeStore: Pick<BusinessContextViewScopeStoreLike, "setActive">,
  scopeId: string,
) {
  viewScopeStore.setActive(scopeId, "business-context-manager")
}

export function createAuthSessionChangedError() {
  const error = new Error("登录上下文已变化")
  error.name = "AuthSessionChangedError"
  return error
}

export function isAuthSessionChangedError(error: unknown) {
  return error instanceof Error && error.name === "AuthSessionChangedError"
}

export function createSystemVersionUnavailableError(reason?: string) {
  const error = new Error(`当前机构版本信息未就绪${reason ? `:${reason}` : ""}`)
  error.name = "BusinessContextVersionUnavailableError"
  return error
}

export async function loadOrganizationsForCurrentSession<T>({
  getToken,
  loadOrganizations,
  applyOrganizations,
  requestOptions,
}: OrganizationSessionLoadOptions<T>) {
  const startedToken = getToken() ?? ""
  if (!startedToken) {
    throw createAuthSessionChangedError()
  }
  const organizations = await loadOrganizations(requestOptions)
  if ((getToken() ?? "") !== startedToken) {
    throw createAuthSessionChangedError()
  }
  applyOrganizations?.(organizations)
  return organizations
}

export function refreshBusinessContextOrganizations(
  viewScopeStore: Pick<BusinessContextViewScopeStoreLike, "setOrganizations">,
  options: OrganizationSessionLoadOptions,
) {
  return runBusinessContextTransaction("refresh-organizations", async (transaction) => {
    const organizations = await loadOrganizationsForCurrentSession({
      ...options,
      requestOptions: { skipBusinessContextBarrier: true },
    })
    transaction.assertCurrent()
    stageBusinessContextOrganizations(viewScopeStore, organizations)
    return organizations
  })
}

export function refreshBusinessContextOrganizationContext(
  viewScopeStore: Pick<
    BusinessContextViewScopeStoreLike,
    | "accountId"
    | "activeScopeId"
    | "preferredScopeId"
    | "legacyArtifactHandoffScopeId"
    | "setActive"
    | "setOrganizations"
  >,
  options: OrganizationSessionLoadOptions<OrganizationContextSnapshot>,
) {
  return runBusinessContextTransaction("refresh-organization-context", async (transaction) => {
    const snapshot = await loadOrganizationsForCurrentSession({
      ...options,
      requestOptions: { skipBusinessContextBarrier: true },
    })
    transaction.assertCurrent()
    stageBusinessContextOrganizations(viewScopeStore, snapshot.organizations)

    const token = options.getToken() ?? ""
    const handoffScopeId = consumeScopeHandoff(
      token,
      Date.now(),
      viewScopeStore.accountId,
    )
    const observedScopeId = snapshot.organizations.find(
      organization => isObservedScopeToken(organization.id, token),
    )?.id
    const resolution = resolveBootstrapScope({
      organizations: snapshot.organizations,
      serverScopeId: snapshot.currentScope?.scopeId,
      handoffScopeId,
      observedScopeId,
      legacyHandoffScopeId: viewScopeStore.legacyArtifactHandoffScopeId,
      preferredScopeId:
        viewScopeStore.preferredScopeId ?? viewScopeStore.activeScopeId,
    })
    if (resolution.scopeId) {
      stageBusinessContextScope(viewScopeStore, resolution.scopeId)
      if (
        resolution.source === "server-token"
        || resolution.source === "artifact-handoff"
        || resolution.source === "observed-token"
      ) {
        markObservedScopeToken(resolution.scopeId, token)
      }
    }
    recordBusinessContextTrace("organization-context.bootstrap-resolved", {
      source: resolution.source,
      scopeId: resolution.scopeId,
      serverScopeId: snapshot.currentScope?.scopeId,
      hadHandoff: Boolean(handoffScopeId),
      hadLegacyHandoff: Boolean(viewScopeStore.legacyArtifactHandoffScopeId),
    })
    return {
      ...snapshot,
      resolution,
    }
  })
}

function resolveActiveScope(
  viewScopeStore: BusinessContextViewScopeStoreLike,
  activeScopeId: string,
  organizations = viewScopeStore.organizations,
) {
  return organizations?.find(scope => scope.id === activeScopeId)
    ?? (viewScopeStore.activeScope?.id === activeScopeId ? viewScopeStore.activeScope : undefined)
}

function resolveAllowedScopeId(
  viewScopeStore: BusinessContextViewScopeStoreLike,
  permissionList: string[],
  candidateScopeId: string,
  organizations = viewScopeStore.organizations,
) {
  if (!Array.isArray(organizations)) {
    return candidateScopeId
  }
  return ensureActiveScopeAllowedForPermissions(
    organizations,
    candidateScopeId,
    permissionList,
  )
}

function shouldFetchShopInfo(scope: OrganizationScope | undefined, scopeId: string) {
  return scope?.type === "shop"
    || scope?.type === "chain-store"
    || scopeId.startsWith("shop-")
    || scopeId.startsWith("chain-store-")
}

function assertScopeMutationDependencies(
  userStore: BusinessContextUserStoreLike,
): asserts userStore is BusinessContextUserStoreLike & {
  switchScopeToken: NonNullable<BusinessContextUserStoreLike["switchScopeToken"]>
  createScopeSwitchRollback: NonNullable<BusinessContextUserStoreLike["createScopeSwitchRollback"]>
  disconnectBusinessWebSocket: NonNullable<BusinessContextUserStoreLike["disconnectBusinessWebSocket"]>
  syncBusinessWebSocket: NonNullable<BusinessContextUserStoreLike["syncBusinessWebSocket"]>
} {
  if (!userStore.switchScopeToken || !userStore.createScopeSwitchRollback) {
    throw new Error("机构登录上下文缺少切换或回滚能力")
  }
  if (!userStore.disconnectBusinessWebSocket || !userStore.syncBusinessWebSocket) {
    throw new Error("机构登录上下文缺少实时连接生命周期能力")
  }
}

function registerDynamicRoutes(router: BusinessContextRouterLike, routes: RouteRecordRaw[]) {
  routes.forEach((route) => {
    if (route.name && router.hasRoute?.(route.name)) return
    router.addRoute(route)
  })
}

async function syncBusinessWebSocketSafely(userStore: BusinessContextUserStoreLike) {
  try {
    await userStore.syncBusinessWebSocket?.()
  } catch (error) {
    console.warn("业务上下文已生效，但实时连接同步失败:", error)
  }
}

function discard(
  reason: string,
  discardReason: BusinessContextDiscardReason,
  suggestedScopeId?: string,
): BusinessContextSyncResult {
  return {
    applied: false,
    reason,
    discardReason,
    ...(suggestedScopeId ? { suggestedScopeId } : {}),
  }
}

function isPreparedContextCurrent(
  deps: Pick<BusinessContextDependencies, "getToken" | "viewScopeStore">,
  token: string,
  committedScopeId: string,
  transaction?: BusinessContextTransaction,
) {
  return (transaction?.isCurrent() ?? true)
    && (deps.getToken() ?? "") === token
    && deps.viewScopeStore.activeScopeId === committedScopeId
}

function resolveContextDiscardReason(
  deps: Pick<BusinessContextDependencies, "getToken" | "viewScopeStore">,
  token: string,
  committedScopeId: string,
  transaction: BusinessContextTransaction,
): BusinessContextDiscardReason | undefined {
  if (!transaction.isCurrent() || (deps.getToken() ?? "") !== token) {
    return "token-changed"
  }
  if (deps.viewScopeStore.activeScopeId !== committedScopeId) {
    return "scope-changed"
  }
}

async function synchronizeBusinessContextInTransaction(
  deps: BusinessContextDependencies,
  options: BusinessContextSyncOptions,
  transaction: BusinessContextTransaction,
): Promise<BusinessContextSyncResult> {
  const startedToken = deps.getToken() ?? ""
  if (!startedToken) return discard(options.reason, "token-changed")

  const committedScopeId = deps.viewScopeStore.activeScopeId
  const candidateScopeId = options.targetScopeId ?? committedScopeId
  let candidateOrganizations = deps.viewScopeStore.organizations
  if (options.loadOrganizations) {
    let organizations: OrganizationScope[] | undefined
    try {
      organizations = await loadOrganizationsForCurrentSession({
        getToken: deps.getToken,
        loadOrganizations: async () => await options.loadOrganizations?.({
          skipBusinessContextBarrier: true,
        }) ?? [],
      })
    } catch (error) {
      if (isAuthSessionChangedError(error)) {
        return discard(options.reason, "token-changed")
      }
      throw error
    }
    if (Array.isArray(organizations)) {
      candidateOrganizations = organizations
    }
    if (deps.viewScopeStore.activeScopeId !== committedScopeId) {
      return discard(options.reason, "scope-changed")
    }
  }
  if (Array.isArray(candidateOrganizations)) {
    const availableScopeId = resolveAllowedScopeId(
      deps.viewScopeStore,
      [],
      candidateScopeId,
      candidateOrganizations,
    )
    if (!availableScopeId) {
      return discard(options.reason, "no-allowed-scope")
    }
    if (availableScopeId !== candidateScopeId) {
      return discard(options.reason, "scope-changed", availableScopeId)
    }
  }

  const rollback = options.rollbackOnFailure === false
    ? undefined
    : deps.userStore.createScopeSwitchRollback?.()
  const systemVersionSnapshot = captureSystemVersionRuntime()
  const organizationsSnapshot = deps.viewScopeStore.organizations
    ? [...deps.viewScopeStore.organizations]
    : undefined
  let systemVersionApplied = false
  let organizationsApplied = false
  let frontendReloadRequired = false
  let frontendReloadSuppressed = false
  let systemVersionActivation: BusinessContextSystemVersionActivation | undefined
  const abort = (discardReason: BusinessContextDiscardReason, suggestedScopeId?: string) => {
    const transactionCurrent = transaction.isCurrent()
    const tokenUnchanged = (deps.getToken() ?? "") === startedToken
    const committedScopeUnchanged = deps.viewScopeStore.activeScopeId === committedScopeId
    if (transactionCurrent && tokenUnchanged && committedScopeUnchanged) {
      rollback?.(startedToken)
      if (systemVersionApplied) {
        restoreSystemVersionRuntime(systemVersionSnapshot)
      }
      if (organizationsApplied && organizationsSnapshot) {
        stageBusinessContextOrganizations(deps.viewScopeStore, organizationsSnapshot)
      }
    }
    return discard(options.reason, discardReason, suggestedScopeId)
  }
  try {
    const targetScope = options.targetScope
      ?? resolveActiveScope(deps.viewScopeStore, candidateScopeId, candidateOrganizations)
    let targetSystemVersion: SystemVersionDTO | undefined
    if (options.syncSystemVersion !== false) {
      const versionLookup = await loadSystemVersionForOrganizationScope(
        targetScope,
        candidateScopeId,
        {
          isCurrent: () =>
            isPreparedContextCurrent(deps, startedToken, committedScopeId, transaction),
          skipBusinessContextBarrier: true,
        },
      )
      if (versionLookup.loaded && versionLookup.version) {
        targetSystemVersion = versionLookup.version
      } else {
        // Version routing is auxiliary context. A missing/temporarily unavailable
        // version must not prevent a valid organization token from loading its
        // permissions. Drop the previous organization snapshot and continue on
        // stable routing; the next refresh can resolve the target version again.
        console.warn(
          `当前机构版本信息未就绪，已按稳定版本继续:${versionLookup.reason ?? "unknown"}`,
        )
        clearSystemVersionCache()
        systemVersionApplied = true
      }
    }
    const versionDiscardReason = resolveContextDiscardReason(
      deps,
      startedToken,
      committedScopeId,
      transaction,
    )
    if (versionDiscardReason) {
      return abort(versionDiscardReason)
    }

    const getInfoOptions = {
      ...options.getInfoOptions,
      skipBusinessContextBarrier: true,
      includeShopBootstrap: true,
      ...(targetSystemVersion
        ? { businessContextSystemVersion: targetSystemVersion }
        : {}),
      businessContextScope: targetScope,
      businessContextScopeId: candidateScopeId,
      isBusinessContextCurrent: () =>
        isPreparedContextCurrent(deps, startedToken, committedScopeId, transaction),
    }
    const preparedUserContext = deps.userStore.prepareBusinessContext
      ? await deps.userStore.prepareBusinessContext(getInfoOptions)
      : undefined
    if (!preparedUserContext) {
      await deps.userStore.getInfo(false, getInfoOptions)
    }
    const identityDiscardReason = resolveContextDiscardReason(
      deps,
      startedToken,
      committedScopeId,
      transaction,
    )
    if (identityDiscardReason) {
      return abort(identityDiscardReason)
    }

    if (options.roleView !== undefined) {
      const roleAccepted = preparedUserContext?.stageActiveRoleView
        ? preparedUserContext.stageActiveRoleView(options.roleView)
        : deps.userStore.stageActiveRoleView?.(
          options.roleView,
          targetScope,
          candidateScopeId,
        ) ?? true
      if (!roleAccepted && options.strictRoleView) {
        throw new Error("所选角色在当前机构已不可用，请重新选择")
      }
    }
    const permissionList = preparedUserContext?.getActivePermissionList()
      ?? deps.userStore.getActivePermissionList(targetScope, candidateScopeId)
    const allowedScopeId = resolveAllowedScopeId(
      deps.viewScopeStore,
      permissionList,
      candidateScopeId,
      candidateOrganizations,
    )
    if (!allowedScopeId && Array.isArray(candidateOrganizations)) {
      return abort("no-allowed-scope")
    }
    if (allowedScopeId !== candidateScopeId) {
      return abort("scope-changed", allowedScopeId)
    }

    const finalScopeId = allowedScopeId || candidateScopeId
    const finalScope = resolveActiveScope(deps.viewScopeStore, finalScopeId, candidateOrganizations)
    const permissionDiscardReason = resolveContextDiscardReason(
      deps,
      startedToken,
      committedScopeId,
      transaction,
    )
    if (permissionDiscardReason) {
      return abort(permissionDiscardReason)
    }

    if (
      shouldFetchShopInfo(finalScope, finalScopeId)
      && !preparedUserContext?.shopBootstrapComplete
    ) {
      if (!deps.userStore.fetchShopInfo) {
        throw new Error("门店业务上下文缺少门店初始化能力")
      }
      await deps.userStore.fetchShopInfo(targetSystemVersion, {
        isCurrent: () =>
          isPreparedContextCurrent(deps, startedToken, committedScopeId, transaction),
        scope: finalScope,
        scopeId: finalScopeId,
        skipBusinessContextBarrier: true,
      })
    }
    const shopDiscardReason = resolveContextDiscardReason(
      deps,
      startedToken,
      committedScopeId,
      transaction,
    )
    if (shopDiscardReason) {
      return abort(shopDiscardReason)
    }

    transaction.assertCurrent()
    const preparedAccountId = resolveBusinessContextAccountId(
      preparedUserContext?.getBusinessContextAccountId?.(),
      deps.userStore.getBusinessContextAccountId?.(),
      deps.viewScopeStore.accountId,
    )
    if (
      !options.deferUserCommit
      && finalScopeId === committedScopeId
      && preparedAccountId
      && preparedAccountId !== deps.viewScopeStore.accountId
    ) {
      const accountCommitted = deps.viewScopeStore.commitContext
        ? deps.viewScopeStore.commitContext(preparedAccountId, finalScopeId)
        : deps.viewScopeStore.bindAccount?.(
          preparedAccountId,
          { preserveRuntimeOnAccountChange: true },
        )
      if (accountCommitted === false) {
        throw new Error("业务上下文已初始化，但无法提交账号上下文")
      }
    }
    if (targetSystemVersion) {
      systemVersionActivation = activateBusinessContextSystemVersion(targetSystemVersion)
      if (!systemVersionActivation.applied) {
        throw createSystemVersionUnavailableError("empty-response")
      }
      frontendReloadRequired = systemVersionActivation.frontendReloadRequired
      frontendReloadSuppressed = Boolean(
        systemVersionActivation.frontendReloadSuppressed,
      )
    }
    systemVersionApplied = systemVersionApplied || Boolean(targetSystemVersion)
    if (candidateOrganizations !== deps.viewScopeStore.organizations) {
      stageBusinessContextOrganizations(deps.viewScopeStore, candidateOrganizations ?? [])
      organizationsApplied = true
    }
    let preparedContextCommitted = false
    const commitPreparedContext = () => {
      if (preparedContextCommitted) return
      preparedUserContext?.commit()
      deps.permissionStore.setRoutes(
        preparedUserContext?.getActiveRoleList() ?? deps.userStore.getActiveRoleList(),
        permissionList,
        createRouteAccessContext({
          activeScope: finalScope,
          permissionList,
        }),
      )
      deps.resetRouter()
      registerDynamicRoutes(deps.router, deps.permissionStore.dynamicRoutes)
      if (options.clearTags !== false) {
        deps.tagsViewStore.delAllVisitedViews()
        deps.tagsViewStore.delAllCachedViews()
      }
      if (options.bumpContextVersion !== false) {
        deps.viewScopeStore.bumpContextVersion?.()
      }
      preparedContextCommitted = true
    }
    if (!options.deferUserCommit) {
      commitPreparedContext()
    }
    if (!options.deferFrontendReload && systemVersionActivation) {
      finalizeBusinessContextSystemVersion(systemVersionActivation)
    }
    if (options.syncWebSocket !== false && !frontendReloadRequired) {
      await syncBusinessWebSocketSafely(deps.userStore)
    }
    return {
      applied: true,
      reason: options.reason,
      activeScopeId: finalScopeId,
      frontendReloadRequired,
      ...(frontendReloadSuppressed ? { frontendReloadSuppressed: true } : {}),
      ...(options.deferUserCommit
        ? {
            commitPreparedContext,
            getActiveRoleView: preparedUserContext?.getActiveRoleView,
            getDefaultHomePath: preparedUserContext?.getDefaultHomePath,
            getBusinessContextAccountId: preparedUserContext?.getBusinessContextAccountId,
          }
        : {}),
    }
  } catch (error) {
    const transactionCurrent = transaction.isCurrent()
    const tokenUnchanged = (deps.getToken() ?? "") === startedToken
    const scopeUnchanged = deps.viewScopeStore.activeScopeId === committedScopeId
    if (transactionCurrent && tokenUnchanged && scopeUnchanged) {
      rollback?.(startedToken)
      if (systemVersionApplied) {
        restoreSystemVersionRuntime(systemVersionSnapshot)
      }
      if (organizationsApplied && organizationsSnapshot) {
        stageBusinessContextOrganizations(deps.viewScopeStore, organizationsSnapshot)
      }
    }
    if (!transactionCurrent || !tokenUnchanged || isAuthSessionChangedError(error)) {
      return discard(options.reason, "token-changed")
    }
    if (!scopeUnchanged) {
      return discard(options.reason, "scope-changed")
    }
    throw error
  }
}

export function synchronizeBusinessContext(
  deps: BusinessContextDependencies,
  options: BusinessContextSyncOptions,
  transaction?: BusinessContextTransaction,
): Promise<BusinessContextSyncResult> {
  if (transaction) {
    return synchronizeBusinessContextInTransaction(deps, options, transaction)
  }
  return runBusinessContextTransaction(
    `synchronize:${options.reason}`,
    async (activeTransaction) => {
      const scopeId = deps.viewScopeStore.activeScopeId
      if (
        !options.ensureScopeToken
        || !scopeId
      ) {
        return synchronizeBusinessContextInTransaction(deps, options, activeTransaction)
      }
      assertScopeMutationDependencies(deps.userStore)

      const rollbackScopeSwitch = deps.userStore.createScopeSwitchRollback?.()
      const previousToken = deps.getToken() ?? ""
      const previousScopeWasObserved = isObservedScopeToken(scopeId, previousToken)
      let switchedToken = ""
      let rollbackAttempted = false
      const rollbackSwitchedContext = async () => {
        if (rollbackAttempted || !switchedToken || !rollbackScopeSwitch) return
        rollbackAttempted = true
        const rolledBack = rollbackScopeSwitch(switchedToken)
        if (rolledBack !== false) {
          stageBusinessContextScope(deps.viewScopeStore, scopeId)
          if (previousScopeWasObserved) {
            markObservedScopeToken(scopeId, previousToken)
            await syncBusinessWebSocketSafely(deps.userStore)
          } else {
            clearObservedScopeToken(scopeId, switchedToken)
          }
        }
      }
      try {
        let targetRoleView = options.roleView
          ?? deps.userStore.getPreferredRoleView?.(scopeId)
          ?? "all"
        if (!isObservedScopeToken(scopeId, deps.getToken() ?? "")) {
          deps.userStore.disconnectBusinessWebSocket?.()
          const issuedRoleView = await deps.userStore.switchScopeToken(
            scopeId,
            targetRoleView,
            {
              callerHandlesAuthFailure: true,
              restorePreferredRole: options.roleView === undefined,
              skipBusinessContextBarrier: true,
            },
          )
          if (issuedRoleView !== undefined) {
            targetRoleView = issuedRoleView
          }
          activeTransaction.assertCurrent()
          switchedToken = deps.getToken() ?? ""
          if (!switchedToken) {
            throw new Error("机构授权上下文未生效")
          }
        }
        const result = await synchronizeBusinessContextInTransaction(
          deps,
          {
            ...options,
            roleView: targetRoleView,
          },
          activeTransaction,
        )
        if (!result.applied) {
          await rollbackSwitchedContext()
        }
        return result
      } catch (error) {
        if (activeTransaction.isCurrent()) {
          await rollbackSwitchedContext()
        }
        throw error
      }
    },
  )
}

export interface PermissionSessionDependencies extends BusinessContextDependencies {
  loadOrganizations?: BusinessContextSyncOptions["loadOrganizations"]
}

export type PermissionSessionRefreshResult = BusinessContextSyncResult

export function refreshPermissionSession(
  reason: string,
  deps: PermissionSessionDependencies,
  options: Pick<BusinessContextSyncOptions, "getInfoOptions"> = {},
  transaction?: BusinessContextTransaction,
): Promise<PermissionSessionRefreshResult> {
  return synchronizeBusinessContext(deps, {
    reason,
    loadOrganizations: deps.loadOrganizations,
    getInfoOptions: options.getInfoOptions,
    syncSystemVersion: true,
    syncWebSocket: true,
  }, transaction)
}

export const resetPermissionSessionForTest = resetBusinessContextManagerForTest

type BusinessContextSwitchRouter = Pick<
  Router,
  "addRoute" | "currentRoute" | "hasRoute" | "replace" | "resolve"
>

export type BusinessContextSwitchResult = {
  changed: boolean
  crossedViewSystem: boolean
  redirected: boolean
  fallbackUsed: boolean
  landingDegraded?: boolean
  superseded?: boolean
}

export interface BusinessContextSwitchOptions {
  scopeId: string
  resolveScopeId?: () => string
  route: RouteLocationNormalizedLoaded
  router: BusinessContextSwitchRouter
  viewScopeStore: BusinessContextViewScopeStoreLike
  chainScopeStore: { reset: () => void }
  userStore: BusinessContextUserStoreLike
  permissionStore: BusinessContextPermissionStoreLike
  tagsViewStore: BusinessContextTagsViewStoreLike
  getToken?: () => string | undefined
  resetRouter?: () => void
  targetRoute?: RouteLocationRaw
  roleView?: any
  strictRoleView?: boolean
}

type BusinessContextSwitchLoading = {
  close: () => void
}

export interface BusinessContextSwitchFeedbackOptions {
  loadingText?: string
  openLoading?: (text: string) => BusinessContextSwitchLoading | undefined
}

function rebuildRoutesFromCurrentUserState(
  options: BusinessContextSwitchOptions,
  activeScope: OrganizationScope | undefined,
) {
  const permissionList = options.userStore.getActivePermissionList()
  options.permissionStore.setRoutes(
    options.userStore.getActiveRoleList(),
    permissionList,
    createRouteAccessContext({
      activeScope,
      permissionList,
    }),
  )
  options.resetRouter?.()
  registerDynamicRoutes(options.router, options.permissionStore.dynamicRoutes)
}

function assertSwitchNavigationDependencies(
  permissionStore: BusinessContextPermissionStoreLike,
): asserts permissionStore is BusinessContextPermissionStoreLike & {
  canAccessRoute: NonNullable<BusinessContextPermissionStoreLike["canAccessRoute"]>
  hasRouteName: NonNullable<BusinessContextPermissionStoreLike["hasRouteName"]>
  getFirstAccessibleRoute: NonNullable<BusinessContextPermissionStoreLike["getFirstAccessibleRoute"]>
} {
  if (
    !permissionStore.canAccessRoute
    || !permissionStore.hasRouteName
    || !permissionStore.getFirstAccessibleRoute
  ) {
    throw new Error("机构切换缺少路由权限依赖")
  }
}

type CommittedBusinessContextSwitch = {
  changed: boolean
  crossedViewSystem: boolean
  routeAtStart: RouteLocationNormalizedLoaded
  targetRoute?: RouteLocationRaw
  defaultRoute?: string
  frontendReloadRequired: boolean
  frontendReloadSuppressed?: boolean
  activeRoleView?: any
  accountId: string
  scopeId: string
}

function runBusinessContextSideEffect(
  failureMessage: string,
  effect: () => boolean | void,
) {
  try {
    if (effect() === false) {
      console.warn(failureMessage)
    }
  } catch (error) {
    console.warn(failureMessage, error)
  }
}

function publishCommittedBusinessContextPreferences(
  options: BusinessContextSwitchOptions,
  context: CommittedBusinessContextSwitch,
) {
  if (context.activeRoleView !== undefined) {
    runBusinessContextSideEffect(
      "机构上下文已生效，但角色偏好保存失败",
      () => options.userStore.setActiveRoleView?.(context.activeRoleView),
    )
  }

  runBusinessContextSideEffect(
    "机构上下文已生效，但机构偏好保存失败",
    () => options.viewScopeStore.commitContext
      ? options.viewScopeStore.commitContext(context.accountId, context.scopeId)
      : options.viewScopeStore.commitActive?.(context.scopeId),
  )
}

function publishCommittedBusinessContextCacheEffects(
  options: BusinessContextSwitchOptions,
) {
  const cacheFailure = "机构上下文已生效，但页面缓存清理失败"
  runBusinessContextSideEffect(cacheFailure, options.chainScopeStore.reset)
  runBusinessContextSideEffect(cacheFailure, options.tagsViewStore.delAllVisitedViews)
  runBusinessContextSideEffect(cacheFailure, options.tagsViewStore.delAllCachedViews)
}

function remountCommittedBusinessContextPage(
  options: BusinessContextSwitchOptions,
) {
  runBusinessContextSideEffect(
    "机构上下文已生效，但当前页面刷新失败",
    () => options.viewScopeStore.bumpContextVersion?.(),
  )
}

async function prepareAndCommitBusinessContextSwitch(
  options: BusinessContextSwitchOptions,
): Promise<CommittedBusinessContextSwitch> {
  const readToken = options.getToken ?? getCurrentToken
  return runBusinessContextTransaction(`switch:${options.scopeId}`, async (transaction) => {
    const requestedScopeId = options.resolveScopeId?.() ?? options.scopeId
    const routeAtStart = options.router.currentRoute.value ?? options.route
    const previousScopeId = options.viewScopeStore.activeScopeId
    const previousScope = resolveScopeById(options.viewScopeStore, previousScopeId)
    const requestedScope = resolveScopeById(options.viewScopeStore, requestedScopeId)
    const changed = previousScopeId !== requestedScopeId

    if (
      Array.isArray(options.viewScopeStore.organizations)
      && (!requestedScope || !isOrganizationScopeSwitchable(requestedScope))
    ) {
      throw new Error("当前机构不可进入，请刷新机构列表后重试")
    }
    assertSwitchNavigationDependencies(options.permissionStore)
    assertScopeMutationDependencies(options.userStore)

    let requestedRoleView = options.roleView
      ?? options.userStore.getPreferredRoleView?.(requestedScopeId)
      ?? "all"
    const previousToken = readToken() ?? ""
    const previousScopeWasObserved = isObservedScopeToken(previousScopeId, previousToken)
    const tokenSwitchRequired = changed
      || options.roleView !== undefined
      || !isObservedScopeToken(requestedScopeId, previousToken)
    const rollbackScopeSwitch = options.userStore.createScopeSwitchRollback!()
    const systemVersionSnapshot = captureSystemVersionRuntime()
    let switchedToken = ""
    let tokenSwitched = false
    let runtimeMutated = false
    let contextCommitted = false
    options.userStore.disconnectBusinessWebSocket?.()

    try {
      if (tokenSwitchRequired) {
        const issuedRoleView = await options.userStore.switchScopeToken!(
          requestedScopeId,
          requestedRoleView,
          {
            callerHandlesAuthFailure: true,
            restorePreferredRole: options.roleView === undefined,
            skipBusinessContextBarrier: true,
          },
        )
        if (issuedRoleView !== undefined) {
          requestedRoleView = issuedRoleView
        }
        transaction.assertCurrent()
        tokenSwitched = true
        switchedToken = readToken() ?? ""
        if (!switchedToken) {
          throw new Error("机构切换成功，但未建立新的登录上下文")
        }
        runtimeMutated = true
      } else if (!previousToken) {
        throw createAuthSessionChangedError()
      }

      runtimeMutated = true
      const syncResult = await synchronizeBusinessContext({
        getToken: readToken,
        router: options.router,
        resetRouter: options.resetRouter ?? (() => undefined),
        viewScopeStore: options.viewScopeStore,
        userStore: options.userStore,
        permissionStore: options.permissionStore,
        tagsViewStore: options.tagsViewStore,
      }, {
        reason: options.roleView === undefined ? "organization-switch" : "role-switch",
        getInfoOptions: {
          callerHandlesAuthFailure: true,
          skipBusinessContextBarrier: true,
        },
        rollbackOnFailure: false,
        syncSystemVersion: true,
        syncWebSocket: false,
        clearTags: false,
        bumpContextVersion: false,
        deferFrontendReload: true,
        deferUserCommit: true,
        roleView: requestedRoleView,
        strictRoleView: options.strictRoleView,
        targetScopeId: requestedScopeId,
        targetScope: requestedScope,
      }, transaction)
      if (!syncResult.applied) {
        throw new Error(`机构上下文同步失败:${syncResult.discardReason}`)
      }
      transaction.assertCurrent()
      const preparedToken = readToken() ?? ""
      if (!preparedToken || preparedToken !== (tokenSwitched ? switchedToken : previousToken)) {
        throw createAuthSessionChangedError()
      }
      if (options.viewScopeStore.activeScopeId !== previousScopeId) {
        throw createAuthSessionChangedError()
      }

      const finalScopeId = syncResult.activeScopeId
      const finalScope = resolveScopeById(options.viewScopeStore, finalScopeId)
      const crossedViewSystem = isCrossViewSystemSwitch(
        previousScope,
        previousScopeId,
        finalScope,
        finalScopeId,
      )
      const activeRoleView = syncResult.getActiveRoleView?.()
        ?? options.userStore.getActiveRoleView?.()
      const preparedAccountId = resolveBusinessContextAccountId(
        syncResult.getBusinessContextAccountId?.(),
        options.userStore.getBusinessContextAccountId?.(),
        options.viewScopeStore.accountId,
      )
      const defaultRoute = syncResult.getDefaultHomePath?.()
        ?? options.userStore.getDefaultHomePath?.()

      // The candidate was prepared against the still-committed scope. Commit its
      // identity/permission snapshot before publishing the target scope; otherwise
      // its own consistency guard observes our staged scope as an external change.
      syncResult.commitPreparedContext?.()
      stageBusinessContextScope(options.viewScopeStore, finalScopeId)
      contextCommitted = true
      return {
        changed,
        crossedViewSystem,
        routeAtStart,
        targetRoute: options.targetRoute,
        defaultRoute,
        frontendReloadRequired: syncResult.frontendReloadRequired,
        frontendReloadSuppressed: syncResult.frontendReloadSuppressed,
        activeRoleView,
        accountId: preparedAccountId,
        scopeId: finalScopeId,
      }
    } catch (error) {
      if (transaction.isCurrent() && !contextCommitted) {
        if (runtimeMutated) {
          let rolledBack = false
          try {
            rolledBack = rollbackScopeSwitch(
              tokenSwitched ? switchedToken : previousToken,
            ) !== false
          } catch (rollbackError) {
            console.warn("机构上下文回滚失败:", rollbackError)
          }
          if (rolledBack) {
            restoreSystemVersionRuntime(systemVersionSnapshot)
            stageBusinessContextScope(options.viewScopeStore, previousScopeId)
            if (previousScopeWasObserved) {
              markObservedScopeToken(previousScopeId, previousToken)
            } else {
              clearObservedScopeToken(
                requestedScopeId,
                tokenSwitched ? switchedToken : previousToken,
              )
            }
            try {
              rebuildRoutesFromCurrentUserState(options, previousScope)
            } catch (routeRollbackError) {
              console.warn("机构上下文路由回滚失败:", routeRollbackError)
            }
            if (previousScopeWasObserved) {
              await syncBusinessWebSocketSafely(options.userStore)
            }
          } else {
            invalidateSystemVersionRuntime()
            clearObservedScopeToken()
            options.userStore.clearScopedPermissionState?.()
          }
        } else {
          restoreSystemVersionRuntime(systemVersionSnapshot)
          stageBusinessContextScope(options.viewScopeStore, previousScopeId)
          if (previousScopeWasObserved) {
            markObservedScopeToken(previousScopeId, previousToken)
            await syncBusinessWebSocketSafely(options.userStore)
          }
        }
      }
      throw error
    }
  }, { manageRequestBarrier: false })
}

type BusinessContextCommandExecution = {
  result: BusinessContextSwitchResult
  committed?: CommittedBusinessContextSwitch
  navigationError?: unknown
}

function createSupersededBusinessContextResult(): BusinessContextSwitchResult {
  return {
    changed: false,
    crossedViewSystem: false,
    redirected: false,
    fallbackUsed: false,
    superseded: true,
  }
}

export function switchBusinessContext(
  options: BusinessContextSwitchOptions,
): Promise<BusinessContextSwitchResult> {
  const commandId = ++businessContextCommandSequence
  latestBusinessContextCommandId = commandId
  const isLatestCommand = () => latestBusinessContextCommandId === commandId
  enqueueBusinessContextRequestBarrier(`switch-command:${options.scopeId}`)
  options.viewScopeStore.beginContextSwitch?.()
  recordBusinessContextTrace("context-switch.command-started", {
    commandId,
    requestedScopeId: options.scopeId,
    activeScopeId: options.viewScopeStore.activeScopeId,
    route: options.router.currentRoute.value.fullPath,
  })

  const execution = businessContextCommandTail
    .catch(() => undefined)
    .then(async (): Promise<BusinessContextCommandExecution> => {
      if (!isLatestCommand()) {
        return { result: createSupersededBusinessContextResult() }
      }

      const committed = await prepareAndCommitBusinessContextSwitch(options)
      recordBusinessContextTrace("context-switch.context-committed", {
        commandId,
        requestedScopeId: options.scopeId,
        committedScopeId: committed.scopeId,
        activeScopeId: options.viewScopeStore.activeScopeId,
        frontendReloadRequired: committed.frontendReloadRequired,
      })
      const baseResult = {
        changed: committed.changed,
        crossedViewSystem: committed.crossedViewSystem,
        redirected: false,
        fallbackUsed: false,
      }
      if (!isLatestCommand()) {
        return {
          result: { ...baseResult, superseded: true },
          committed,
        }
      }

      publishCommittedBusinessContextPreferences(options, committed)
      publishCommittedBusinessContextCacheEffects(options)
      if (committed.frontendReloadRequired) {
        return {
          result: baseResult,
          committed,
        }
      }
      const permissionStore = options.permissionStore
      assertSwitchNavigationDependencies(permissionStore)
      let navigation = {
        redirected: false,
        fallbackUsed: false,
      }
      let navigationError: unknown
      try {
        navigation = await navigateAfterBusinessContextSwitch({
          route: committed.routeAtStart,
          router: options.router,
          permissionStore,
          targetRoute: committed.targetRoute,
          defaultRoute: committed.defaultRoute,
          crossedViewSystem: committed.crossedViewSystem,
        })
      } catch (error) {
        navigationError = error
      }

      if (
        isLatestCommand()
        && options.router.currentRoute.value.path
        === committed.routeAtStart.path
      ) {
        remountCommittedBusinessContextPage(options)
      }
      if (isLatestCommand() && !committed.frontendReloadRequired) {
        await syncBusinessWebSocketSafely(options.userStore)
      }
      return {
        result: {
          ...baseResult,
          ...navigation,
          ...(!isLatestCommand() ? { superseded: true } : {}),
        },
        committed,
        navigationError,
      }
    })

  let commandError: unknown
  const settledExecution = execution.then(
    value => value,
    (error) => {
      commandError = error
      recordBusinessContextTrace("context-switch.command-failed", {
        commandId,
        requestedScopeId: options.scopeId,
        activeScopeId: options.viewScopeStore.activeScopeId,
        error,
      })
      throw error
    },
  )
  const boundarySettled = settledExecution.finally(() => {
    options.viewScopeStore.endContextSwitch?.()
    finishBusinessContextRequestBarrier(commandError)
    recordBusinessContextTrace("context-switch.boundary-settled", {
      commandId,
      requestedScopeId: options.scopeId,
      activeScopeId: options.viewScopeStore.activeScopeId,
      failed: commandError !== undefined,
    })
  })
  const command = boundarySettled.then((value) => {
    if (value.committed && isLatestCommand()) {
      if (value.committed.frontendReloadRequired) {
        writeScopeHandoff(
          value.committed.scopeId,
          (options.getToken ?? getCurrentToken)() ?? "",
          Date.now(),
          value.committed.accountId,
        )
      }
      finalizeBusinessContextSystemVersion({
        applied: true,
        frontendReloadRequired: value.committed.frontendReloadRequired,
        frontendReloadSuppressed: value.committed.frontendReloadSuppressed,
      })
    }
    if (value.navigationError !== undefined) {
      console.warn("机构上下文已生效，但页面落地失败", value.navigationError)
      return {
        ...value.result,
        landingDegraded: true,
      }
    }
    recordBusinessContextTrace("context-switch.command-completed", {
      commandId,
      requestedScopeId: options.scopeId,
      activeScopeId: options.viewScopeStore.activeScopeId,
      route: options.router.currentRoute.value.fullPath,
      result: value.result,
    })
    return value.result
  })
  businessContextCommandTail = command
  return command
}

export async function switchBusinessContextWithFeedback(
  options: BusinessContextSwitchOptions,
  feedback: BusinessContextSwitchFeedbackOptions = {},
): Promise<BusinessContextSwitchResult> {
  const loading = feedback.openLoading?.(
    feedback.loadingText ?? "正在切换机构视图并刷新菜单...",
  )
  try {
    return await switchBusinessContext(options)
  } finally {
    loading?.close()
  }
}
